import {useCallback, useRef, useState} from 'react'
import {useCLIPromptState} from './use-cli-prompt-state'
import {useEventsPromptState} from './use-events-prompt-state'
import {useEventsSchemaEnabled} from './use-events-schema-enabled'
import {useAhpPromptState} from './use-ahp-prompt-state'
import {useCreateCommand} from './useCreateCommand'
import {useOptimisticLogs} from '../contexts/OptimisticLogsContext'
import {COMMAND_TYPES} from '../constants/command-types'
import type {NormalizedPrompt} from '../components/session-logs/prompt-log-utils'
import {findSessionFanoutTargets} from '../utils/permission-prompt-helpers'
import {SessionState, type CommandType} from '../types/session'

export interface UsePromptResponseSubmissionOptions {
  /** The session whose prompt should be monitored and responded to. */
  sessionId: string | undefined
  /** The task ID for events-based prompt detection (used when copilot_mission_control_session_events_ui is enabled). */
  taskId?: string | undefined
  /** Whether to enable the underlying CLI prompt state hook (typically: !!sessionId
   *  AND the session is in a state that can accept user input). */
  enabled: boolean
  /** Current session state. */
  sessionState?: SessionState
  /**
   * When true, the session is AHP-backed (identity): the events/logs prompt
   * sources are disabled entirely so an AHP task is never driven by event logs.
   */
  isAhpSession?: boolean
  /**
   * When true, the AHP relay is reachable (environment online + AHP-capable), so
   * the relay-backed prompt source is acquired. Always implies isAhpSession.
   */
  reachable?: boolean
  /** MC environment id the AHP session is bound to (required when reachable). */
  environmentId?: string | undefined
  /** Optional callback invoked when the prompt-response createCommand mutation
   *  fails, after the optimistic record has been rewound. Use this to surface
   *  a user-visible error in the consumer (e.g. setFailedToSubmit). */
  onError?: () => void
}

export interface PromptResponseSubmission {
  /** The current pending prompt, or null when none. */
  pendingPrompt: NormalizedPrompt | null
  /** Zero-based index of the selected pending prompt. */
  pendingPromptIndex: number
  /** Total number of unresolved prompts. */
  pendingPromptCount: number
  /** True while the underlying logs query is loading. */
  isLoading: boolean
  /**
   * True when the pending prompt is older than the resume window AND the
   * session is paused (`waiting_for_user`, i.e. CCA only).
   */
  isExpired: boolean
  /** True while the currently visible prompt has a response command in flight. */
  isPending: boolean
  /** Dismiss the current prompt without sending a response. */
  dismissPrompt: () => void
  /** Select the previous unresolved prompt, if one exists. */
  goToPreviousPrompt: () => void
  /** Select the next unresolved prompt, if one exists. */
  goToNextPrompt: () => void
  /** Submit a response for the pending prompt. No-op when there is no pending
   *  prompt, a previous response is in flight, or sessionId is missing. */
  handlePromptRespond: (toolCallId: string, response: unknown, commandType: CommandType) => void
}

interface PendingPromptResponseSubmission {
  optimisticId: string | null
  resolvedToolCallId: string | null
  sessionFanoutResolvedToolCallIds: string[]
  sessionFanoutOptimisticIds: string[]
}

interface PromptResponseSubmissionSnapshot {
  submissionId: string
}

/**
 * Wires together {@link useCLIPromptState}, {@link useOptimisticLogs}, and a
 * dedicated {@link useCreateCommand} mutation for components that render an
 * interactive prompt (permission_request, ask_user, exit_plan_mode) and submit
 * the user's response.
 *
 * Resolution is optimistic: the prompt is marked resolved synchronously on
 * click, and the rollback in `onError` re-shows it if the underlying
 * createCommand mutation fails.
 */
export function usePromptResponseSubmission({
  sessionId,
  taskId,
  enabled,
  sessionState,
  isAhpSession = false,
  reachable = false,
  environmentId,
  onError,
}: UsePromptResponseSubmissionOptions): PromptResponseSubmission {
  const useEventsUI = useEventsSchemaEnabled()

  // Logs-based prompt detection (disabled when events UI or AHP is active)
  const logsPrompt = useCLIPromptState({
    sessionId,
    enabled: enabled && !useEventsUI && !isAhpSession,
    sessionState,
  })

  // Events-based prompt detection (disabled when logs UI or AHP is active)
  const eventsPrompt = useEventsPromptState({
    taskId,
    sessionId,
    enabled: enabled && useEventsUI && !isAhpSession,
  })

  // AHP-native prompt detection: the only source for an AHP task. Acquires the
  // shared relay only when the host is reachable (matching the native timeline);
  // while unreachable it stays inert rather than falling back to events/logs.
  const ahpPrompt = useAhpPromptState({
    taskId,
    environmentId,
    sessionId,
    enabled: enabled && isAhpSession && reachable,
  })

  const {
    pendingPrompt,
    pendingPromptIndex,
    pendingPromptCount,
    isLoading,
    isExpired,
    markPromptResolved,
    unmarkPromptResolved,
    dismissPrompt,
    goToPreviousPrompt,
    goToNextPrompt,
  } = isAhpSession ? ahpPrompt : useEventsUI ? eventsPrompt : logsPrompt
  const {addOptimisticResponse, removeOptimisticResponse} = useOptimisticLogs()
  const pendingSubmissionsRef = useRef(new Map<string, PendingPromptResponseSubmission>())
  const pendingSubmissionIdsByPromptIdRef = useRef(new Map<string, string>())
  const [pendingPromptIds, setPendingPromptIds] = useState<Set<string>>(() => new Set())

  const addPendingSubmission = useCallback((submissionId: string, submission: PendingPromptResponseSubmission) => {
    pendingSubmissionsRef.current.set(submissionId, submission)
    const promptIds = getSubmissionPromptIds(submission)
    for (const promptId of promptIds) {
      pendingSubmissionIdsByPromptIdRef.current.set(promptId, submissionId)
    }
    setPendingPromptIds(prev => {
      const next = new Set(prev)
      for (const promptId of promptIds) {
        next.add(promptId)
      }
      return next
    })
  }, [])

  const clearPendingSubmission = useCallback((submissionId: string, submission: PendingPromptResponseSubmission) => {
    pendingSubmissionsRef.current.delete(submissionId)
    const promptIds = getSubmissionPromptIds(submission)
    for (const promptId of promptIds) {
      pendingSubmissionIdsByPromptIdRef.current.delete(promptId)
    }
    setPendingPromptIds(prev => {
      let changed = false
      const next = new Set(prev)
      for (const promptId of promptIds) {
        changed = next.delete(promptId) || changed
      }
      return changed ? next : prev
    })
  }, [])

  const {createCommand} = useCreateCommand({
    onSuccess: (_command, snapshot) => {
      const submissionId = getSubmissionId(snapshot)
      if (!submissionId) return
      const pendingSubmission = pendingSubmissionsRef.current.get(submissionId)
      if (!pendingSubmission) return

      clearPendingSubmission(submissionId, pendingSubmission)
    },
    onError: (_error, snapshot) => {
      const submissionId = getSubmissionId(snapshot)
      if (!submissionId) return
      const pendingSubmission = pendingSubmissionsRef.current.get(submissionId)
      if (!pendingSubmission) return
      if (pendingSubmission.optimisticId) {
        removeOptimisticResponse(pendingSubmission.optimisticId)
      }
      if (pendingSubmission.resolvedToolCallId) {
        unmarkPromptResolved(pendingSubmission.resolvedToolCallId)
      }
      for (const id of pendingSubmission.sessionFanoutOptimisticIds) {
        removeOptimisticResponse(id)
      }
      for (const tcid of pendingSubmission.sessionFanoutResolvedToolCallIds) {
        unmarkPromptResolved(tcid)
      }
      clearPendingSubmission(submissionId, pendingSubmission)
      onError?.()
    },
  })

  const handlePromptRespond = useCallback(
    (toolCallId: string, response: unknown, commandType: CommandType) => {
      if (!pendingPrompt || pendingSubmissionIdsByPromptIdRef.current.has(toolCallId) || !sessionId) return

      // AHP sessions answer over the relay: mark the prompt resolved so it hides
      // immediately, then dispatch `chat/inputCompleted`. There is no MC command
      // queue or optimistic-logs record in this path — the relay's own state
      // update (the reducer drops the completed request) is the source of truth.
      if (isAhpSession) {
        markPromptResolved(toolCallId)
        try {
          ahpPrompt.respond(toolCallId, response, commandType)
        } catch {
          unmarkPromptResolved(toolCallId)
          onError?.()
        }
        return
      }

      // Only surface the "Resuming session…" indicator when the click is
      // recovering a paused (waiting_for_user) session. CLI sessions don't
      // pause, so showing the indicator there is misleading.
      const fromPausedSession = sessionState === SessionState.WaitingForUser

      const optimisticId = addOptimisticResponse({
        toolCallId,
        response,
        commandType,
        fromPausedSession,
      })
      markPromptResolved(toolCallId)
      // Find sibling prompts that the runtime will auto-resolve when
      // we send the triggering prompt's scope=session approval. We
      // optimistically resolve them in the UI (markPromptResolved +
      // addOptimisticResponse) so all prompts hide immediately, but we
      // send only the SINGLE triggering command to the server.
      //
      // Sending a single command (instead of N) avoids the resumed-session
      // N² problem where N concurrent permission_response
      // commands spawned N resume sessions, each independently
      // re-fanning the approval to all N siblings.
      const sessionFanoutTargets =
        commandType === COMMAND_TYPES.PERMISSION_RESPONSE
          ? findSessionFanoutTargets(pendingPrompt, response, logsPrompt.logs, toolCallId)
          : []
      const pendingSubmission: PendingPromptResponseSubmission = {
        optimisticId,
        resolvedToolCallId: toolCallId,
        sessionFanoutResolvedToolCallIds: [],
        sessionFanoutOptimisticIds: [],
      }
      for (const target of sessionFanoutTargets) {
        markPromptResolved(target.toolCallId)
        pendingSubmission.sessionFanoutResolvedToolCallIds.push(target.toolCallId)
        const optId = addOptimisticResponse({
          toolCallId: target.toolCallId,
          response: {...(response as object), promptId: target.toolCallId},
          commandType,
          inferred: true,
          fromPausedSession,
        })
        pendingSubmission.sessionFanoutOptimisticIds.push(optId)
      }
      addPendingSubmission(toolCallId, pendingSubmission)

      // Forward the prompt-specific command type (e.g. PERMISSION_RESPONSE,
      // ASK_USER_RESPONSE) so Mission Control can route the command correctly:
      // CCAv3 steering filters on `command.type !== 'user_message'` to avoid
      // injecting prompt responses as raw user steering messages.
      createCommand({
        sessionId,
        content: JSON.stringify(response),
        type: commandType,
        snapshot: {submissionId: toolCallId} satisfies PromptResponseSubmissionSnapshot,
      })
    },
    [
      pendingPrompt,
      sessionId,
      sessionState,
      isAhpSession,
      ahpPrompt,
      unmarkPromptResolved,
      onError,
      createCommand,
      addOptimisticResponse,
      markPromptResolved,
      logsPrompt.logs,
      addPendingSubmission,
    ],
  )

  return {
    pendingPrompt,
    pendingPromptIndex,
    pendingPromptCount,
    isLoading,
    isExpired,
    isPending: pendingPrompt ? pendingPromptIds.has(pendingPrompt.toolCallId) : false,
    dismissPrompt,
    goToPreviousPrompt,
    goToNextPrompt,
    handlePromptRespond,
  }
}

function getSubmissionPromptIds(submission: PendingPromptResponseSubmission): string[] {
  const promptIds = submission.resolvedToolCallId ? [submission.resolvedToolCallId] : []
  promptIds.push(...submission.sessionFanoutResolvedToolCallIds)
  return promptIds
}

function getSubmissionId(snapshot: unknown): string | undefined {
  if (!snapshot || typeof snapshot !== 'object') return undefined
  const submissionId = (snapshot as PromptResponseSubmissionSnapshot).submissionId
  return typeof submissionId === 'string' ? submissionId : undefined
}
